import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import DoctorService from "../../services/doctorService";
import PermissionGroupService from "../../services/doctorServices/PermissionGroupService";
import { useParams, useNavigate } from "react-router-dom";
import Headeer from "../../components/Headeer";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import SecurityIcon from "@mui/icons-material/Security";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import EditIcon from "@mui/icons-material/Edit";
import TextField from "@mui/material/TextField";

export default function DoctorDoctorProfile() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams();
  const navigate = useNavigate();
  const doctorService = new DoctorService();
  const permissionService = new PermissionGroupService();
  const [doctorInfo, setDoctorInfo] = useState({});
  const [doctorGroups, setDoctorGroups] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [groupToRemove, setGroupToRemove] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [currentDoctor, setCurrentDoctor] = useState(null);
  const [editRoleTitleDialogOpen, setEditRoleTitleDialogOpen] = useState(false);
  const [roleTitleValue, setRoleTitleValue] = useState("");
  const [roleTitleError, setRoleTitleError] = useState("");

  useEffect(() => {
    const fetchCurrentDoctor = async () => {
      try {
        const response = await doctorService.getDoctorInfo();
        const currentDoctorData = response?.doctor || response || {};
        setCurrentDoctor(currentDoctorData);
      } catch (error) {
        console.error("Error fetching current doctor info:", error);
      }
    };
    fetchCurrentDoctor();
  }, []);

  useEffect(() => {
    const fetchDoctorInfo = async () => {
      try {
        const response = await doctorService.getDoctorInfoById(id);
        console.log("Doctor Info Response:", response);
        // Backend returns { doctor: {...} } format, extract doctor object
        const doctorData = response?.doctor || response;
        setDoctorInfo(doctorData || {});
      } catch (error) {
        console.error("Error fetching doctor info:", error);
        setSnackbar({
          open: true,
          message: error.response?.data?.error || "Error fetching doctor information",
          severity: "error",
        });
        setDoctorInfo({});
      }
    };
    if (id) {
      fetchDoctorInfo();
      fetchDoctorGroups();
    }
  }, [id]);

  const fetchDoctorGroups = async () => {
    try {
      const groups = await permissionService.getDoctorGroups(id);
      setDoctorGroups(groups);
    } catch (error) {
      console.error("Error fetching doctor groups:", error);
    }
  };

  const fetchAvailableGroups = async () => {
    try {
      const allGroups = await permissionService.getAllGroups();
      // Filter out doctor groups and already assigned groups
      const available = allGroups.filter(group => 
        group.type === 'doctor' && !doctorGroups.some(dg => dg.id === group.id)
      );
      setAvailableGroups(available);
    } catch (error) {
      console.error("Error fetching available groups:", error);
    }
  };

  const handleOpenAssignDialog = async () => {
    await fetchAvailableGroups();
    setAssignDialogOpen(true);
  };

  const handleAssignGroup = async (groupId) => {
    try {
      setLoading(true);
      await permissionService.assignGroupToDoctor(id, groupId);
      setSnackbar({
        open: true,
        message: "Permission group assigned successfully",
        severity: "success",
      });
      setAssignDialogOpen(false);
      fetchDoctorGroups();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Error assigning group";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGroupClick = (groupId) => {
    const group = doctorGroups.find(g => g.id === groupId);
    setGroupToRemove({ id: groupId, name: group?.label || group?.name || "this permission group" });
    setRemoveDialogOpen(true);
  };

  const handleRemoveGroupConfirm = async () => {
    if (!groupToRemove) return;
    
    setRemoveDialogOpen(false);
    try {
      setLoading(true);
      await permissionService.removeGroupFromDoctor(id, groupToRemove.id);
      setSnackbar({
        open: true,
        message: "Permission group removed successfully",
        severity: "success",
      });
      fetchDoctorGroups();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Error removing group";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setLoading(false);
      setGroupToRemove(null);
    }
  };

  const capitalizeWords = (str) => {
    return str.trim().replace(/\b\w/g, char => char.toUpperCase());
  };

  const canEditRoleTitle = useMemo(() => {
    // Admin doktor kendi eklediği alt doktorun title'ını düzenleyebilir
    if (!currentDoctor || !doctorInfo) return false;
    
    const isAdmin = currentDoctor.role === 'admin';
    const currentDoctorId = String(currentDoctor.id || '');
    const doctorCreatedBy = String(doctorInfo.created_by || '');
    const isCreatedByCurrentDoctor = doctorCreatedBy && currentDoctorId && doctorCreatedBy === currentDoctorId;
    
    const canEdit = isAdmin && currentDoctorId && isCreatedByCurrentDoctor;
    
    return canEdit;
  }, [currentDoctor, doctorInfo]);

  const handleOpenEditRoleTitleDialog = () => {
    setRoleTitleValue(doctorInfo?.role_title || "");
    setRoleTitleError("");
    setEditRoleTitleDialogOpen(true);
  };

  const handleCloseEditRoleTitleDialog = () => {
    setEditRoleTitleDialogOpen(false);
    setRoleTitleValue("");
    setRoleTitleError("");
  };

  const handleSaveRoleTitle = async () => {
    if (!roleTitleValue.trim()) {
      setRoleTitleError("Role title is required");
      return;
    }

    try {
      setLoading(true);
      const capitalizedRoleTitle = capitalizeWords(roleTitleValue);
      await doctorService.updateDoctor(id, { role_title: capitalizedRoleTitle });
      
      // Refresh doctor info
      const response = await doctorService.getDoctorInfoById(id);
      const doctorData = response?.doctor || response;
      setDoctorInfo(doctorData || {});
      
      setSnackbar({
        open: true,
        message: "Role title updated successfully",
        severity: "success",
      });
      handleCloseEditRoleTitleDialog();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || "Error updating role title";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const formattedRole = useMemo(() => {
    if (!doctorInfo?.role) return "—";
    return doctorInfo.role.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  }, [doctorInfo]);

  const overviewItems = useMemo(
    () => [
      {
        label: "Patients",
        value: doctorInfo?.patientsCount ?? "—",
        icon: <AssignmentIndOutlinedIcon sx={{ fontSize: 24 }} />,
        gradient: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
      },
      {
        label: "Files",
        value: doctorInfo?.filesCount ?? "—",
        icon: <WorkspacesOutlinedIcon sx={{ fontSize: 24 }} />,
        gradient: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.blueAccent[700]} 100%)`,
      },
      {
        label: "Role",
        value: formattedRole,
        icon: <LocalHospitalOutlinedIcon sx={{ fontSize: 24 }} />,
        gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
      },
      {
        label: "Role Title",
        value: doctorInfo?.role_title ?? "—",
        icon: <WorkspacePremiumIcon sx={{ fontSize: 24 }} />,
        gradient: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.primary[600]} 100%)`,
      },
    ],
    [colors, doctorInfo, formattedRole]
  );

  return (
    <Box sx={{ marginLeft: 2, marginRight: 2, py: 4 }}>
      <Headeer title="Doctor Profile" subtitle="View doctor information and permissions" />
      
      <Box
        sx={{
          width: "100%",
          margin: "0 auto",
          maxWidth: "1100px",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Card
          sx={{
            borderRadius: 4,
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.blueAccent[800]} 100%)`,
            color: colors.grey[100],
            boxShadow: `0 24px 48px rgba(15, 23, 42, 0.35)`,
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4 }}>
            <Stack
              alignItems="center"
              spacing={2.5}
              sx={{
                flexShrink: 0,
                minWidth: { xs: "auto", md: 260 },
              }}
            >
              <Avatar
                sx={{
                  width: 140,
                  height: 140,
                  borderRadius: "32px",
                  boxShadow: `0 16px 40px rgba(15, 23, 42, 0.35)`,
                  border: `4px solid rgba(255,255,255,0.2)`,
                  background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
                  fontSize: 48,
                  fontWeight: 700,
                }}
              >
                {doctorInfo?.name?.[0]?.toUpperCase()}{doctorInfo?.surname?.[0]?.toUpperCase()}
              </Avatar>
              <Box textAlign="center">
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {doctorInfo?.name ? `${doctorInfo.name} ${doctorInfo.surname ?? ""}` : "Doctor"}
                </Typography>
                {doctorInfo?.role_title && (
                  <Typography
                    variant="h6"
                    sx={{
                      mt: 1,
                      color: colors.grey[200],
                      fontWeight: 600,
                      letterSpacing: 0.3,
                    }}
                  >
                    {doctorInfo.role_title}
                  </Typography>
                )}
                <Chip
                  label={formattedRole}
                  sx={{
                    mt: 1.5,
                    px: 2.5,
                    py: 0.5,
                    fontWeight: 600,
                    letterSpacing: 0.4,
                    color: colors.grey[100],
                    background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
                    boxShadow: `0 10px 24px rgba(34, 197, 94, 0.45)`,
                  }}
                />
              </Box>
            </Stack>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: `${colors.primary[300]}55`,
                borderWidth: 1,
                display: { xs: "none", md: "block" },
              }}
            />

            <Grid container spacing={3} sx={{ flexGrow: 1 }}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="overline" sx={{ color: `${colors.grey[100]}99`, letterSpacing: 1 }}>
                      Contact
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" mt={0.8}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.blueAccent[500]}88`,
                        }}
                      >
                        <PersonOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          Full Name
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {doctorInfo?.name} {doctorInfo?.surname}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.blueAccent[500]}88`,
                        }}
                      >
                        <EmailOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          Email
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {doctorInfo?.email ?? "—"}
                        </Typography>
                      </Box>
                    </Stack>
                    {doctorInfo?.phone_number && (
                      <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                        <Avatar
                          sx={{
                            width: 42,
                            height: 42,
                            backgroundColor: `${colors.blueAccent[500]}88`,
                          }}
                        >
                          <PhoneOutlinedIcon sx={{ color: colors.grey[100] }} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                            Phone Number
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {doctorInfo.phone_number}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="overline" sx={{ color: `${colors.grey[100]}99`, letterSpacing: 1 }}>
                      Specialization
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" mt={0.8}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: `${colors.greenAccent[500]}88`,
                        }}
                      >
                        <LocalHospitalOutlinedIcon sx={{ color: colors.grey[100] }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          Field
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {doctorInfo?.specialization ?? "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {overviewItems.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.label}>
              <Card
                sx={{
                  borderRadius: 4,
                  height: "100%",
                  background: item.gradient,
                  color: colors.grey[100],
                  boxShadow: `0 22px 44px rgba(15, 23, 42, 0.3)`,
                  position: "relative",
                }}
              >
                {item.label === "Role Title" && canEditRoleTitle && (
                  <Tooltip title="Edit Role Title">
                    <IconButton
                      size="small"
                      onClick={handleOpenEditRoleTitleDialog}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: colors.grey[100],
                        backgroundColor: "rgba(255,255,255,0.15)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.25)",
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: "rgba(255,255,255,0.25)",
                        color: "white",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      {item.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ opacity: 0.85 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Permission Groups Section */}
        <Card
          sx={{
            borderRadius: 4,
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
            boxShadow: `0 22px 44px rgba(15, 23, 42, 0.3)`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
                  }}
                >
                  <SecurityIcon sx={{ color: "white", fontSize: 24 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 700 }}>
                    Permission Groups
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.5 }}>
                    Assigned permission groups for this doctor
                  </Typography>
                  {doctorGroups.length > 1 && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: colors.greenAccent[300], 
                        mt: 1, 
                        display: "block",
                        fontStyle: "italic",
                        fontSize: "0.75rem"
                      }}
                    >
                      ℹ️ When multiple groups are assigned, the doctor receives the union (combined) permissions from all groups.
                    </Typography>
                  )}
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAssignDialog}
                sx={{
                  background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                  color: "white",
                  px: 3,
                  py: 1,
                  borderRadius: "10px",
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 16px ${colors.greenAccent[500]}60`,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Assign Group
              </Button>
            </Box>

            <Divider sx={{ mb: 3, backgroundColor: `${colors.grey[700]}40` }} />

            {doctorGroups.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  px: 3,
                  borderRadius: "12px",
                  background: `${colors.blueAccent[800]}10`,
                  border: `1px dashed ${colors.grey[500]}50`,
                }}
              >
                <SecurityIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
                <Typography variant="h6" sx={{ color: "#475569", mb: 0.5 }}>
                  No Permission Groups Assigned
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  This doctor has no permission groups yet. Click "Assign Group" to add one.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {doctorGroups.map((group) => (
                  <Grid item xs={12} sm={6} md={4} key={group.id}>
                    <Card
                      sx={{
                        borderRadius: "12px",
                        background: "white",
                        border: `1px solid ${colors.grey[300]}`,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 8px 24px rgba(0, 0, 0, 0.15)`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
                              }}
                            >
                              <AdminPanelSettingsIcon sx={{ color: "white", fontSize: 20 }} />
                            </Avatar>
                            <Box>
                              <Typography variant="h6" sx={{ color: "#1e293b", fontWeight: 700, fontSize: "0.95rem" }}>
                                {group.label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#64748b", fontFamily: "monospace" }}>
                                {group.name}
                              </Typography>
                            </Box>
                          </Box>
                          <Tooltip title="Remove Group">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveGroupClick(group.id)}
                              disabled={loading}
                              sx={{
                                width: 28,
                                height: 28,
                                backgroundColor: `${colors.redAccent[700]}20`,
                                "&:hover": {
                                  backgroundColor: `${colors.redAccent[700]}40`,
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16, color: colors.redAccent[500] }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {group.description && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#475569",
                              fontSize: "0.8rem",
                              lineHeight: 1.5,
                            }}
                          >
                            {group.description}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Assign Group Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle sx={{ color: colors.grey[100], fontWeight: 700 }}>
          Assign Permission Group
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={40} sx={{ color: colors.blueAccent[500] }} />
            </Box>
          ) : availableGroups.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <SecurityIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
              <Typography variant="body1" sx={{ color: colors.grey[300] }}>
                No available permission groups to assign
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {availableGroups.map((group) => (
                <Card
                  key={group.id}
                  onClick={() => handleAssignGroup(group.id)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "12px",
                    background: "white",
                    border: `1px solid ${colors.grey[300]}`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateX(4px)",
                      boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15)`,
                      borderColor: colors.blueAccent[500],
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
                        }}
                      >
                        <AdminPanelSettingsIcon sx={{ color: "white", fontSize: 22 }} />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ color: "#1e293b", fontWeight: 700, fontSize: "0.95rem" }}>
                          {group.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          {group.description || group.name}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setAssignDialogOpen(false)}
            sx={{
              color: colors.grey[300],
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Group Confirmation Dialog */}
      <ConfirmationDialog
        open={removeDialogOpen}
        onClose={() => {
          setRemoveDialogOpen(false);
          setGroupToRemove(null);
        }}
        onConfirm={handleRemoveGroupConfirm}
        title="Remove Permission Group"
        description={`Are you sure you want to remove "${groupToRemove?.name}" from this doctor? This action cannot be undone.`}
      />

      {/* Edit Role Title Dialog */}
      <Dialog
        open={editRoleTitleDialogOpen}
        onClose={handleCloseEditRoleTitleDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle sx={{ color: colors.grey[100], fontWeight: 700 }}>
          Edit Role Title
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Role Title *"
            value={roleTitleValue}
            onChange={(e) => {
              setRoleTitleValue(e.target.value);
              setRoleTitleError("");
            }}
            onBlur={(e) => {
              const value = e.target.value;
              if (value.trim()) {
                const capitalizedValue = capitalizeWords(value);
                setRoleTitleValue(capitalizedValue);
              }
            }}
            error={Boolean(roleTitleError)}
            helperText={roleTitleError || "Enter the role title (e.g., Senior Doctor, Junior Doctor)"}
            variant="filled"
            sx={{
              mt: 2,
              "& .MuiInputLabel-root": {
                color: `${colors.grey[100]}aa`,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: colors.blueAccent[400],
              },
              "& .MuiFilledInput-root": {
                backgroundColor: `${colors.primary[600]}cc`,
                borderRadius: 2,
                color: colors.grey[100],
                "&:hover": {
                  backgroundColor: `${colors.primary[600]}ee`,
                },
                "&.Mui-focused": {
                  backgroundColor: `${colors.primary[600]}ff`,
                  boxShadow: `0 0 0 3px ${colors.blueAccent[400]}33`,
                },
              },
              "& .MuiFilledInput-input": {
                color: colors.grey[100],
              },
              "& .MuiFormHelperText-root": {
                color: `${colors.grey[100]}aa`,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseEditRoleTitleDialog}
            disabled={loading}
            sx={{
              color: colors.grey[300],
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRoleTitle}
            disabled={loading}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
              },
              "&:disabled": {
                background: colors.grey[700],
              },
            }}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

